/**
 * Ultra-smooth scroll utility for internal navigation with custom easing
 */
import { SoundEffect, playSound } from './soundEffects';

/**
 * Scrolls smoothly to specified target with sound effects
 * @param target - The target selector or element to scroll to
 * @param offset - Optional offset from the top of the target
 * @param duration - Optional duration in milliseconds
 * @param playSound - Optional flag to enable/disable sound (enabled by default)
 */
export const smoothScrollTo = (
  target: string | HTMLElement,
  offset = 0,
  duration = 1000,
  enableSound = true
): void => {
  let targetElement: HTMLElement | null = null;

  // Handle both string selectors and direct elements
  if (typeof target === 'string') {
    // Check if the string is an ID without the # prefix
    if (!target.startsWith('#') && !target.startsWith('.')) {
      // Try to find the element by ID first
      targetElement = document.getElementById(target);

      // If not found by ID, try as a regular selector
      if (!targetElement) {
        targetElement = document.querySelector(`#${target}`) as HTMLElement;
      }
    } else {
      // Regular CSS selector
      targetElement = document.querySelector(target) as HTMLElement;

      // If not found and starts with #, try without the # as an ID
      if (!targetElement && target.startsWith('#')) {
        const idWithoutHash = target.substring(1);
        targetElement = document.getElementById(idWithoutHash);
      }
    }
  } else {
    targetElement = target;
  }

  if (!targetElement) {
    console.warn('Smooth scroll target not found:', target);
    return;
  }

  // Calculate header height to offset for fixed header
  const header = document.querySelector('header');
  const headerHeight = header ? header.clientHeight + 20 : 0; // Add extra padding
  const offsetWithHeader = offset + headerHeight;

  const startPosition = window.scrollY;
  const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - offsetWithHeader;
  const distance = targetPosition - startPosition;
  let startTime: number | null = null;

  // Play sound immediately for better responsiveness
  // The sound volume is based on the scroll distance
  if (enableSound) {
    const normalizedDistance = Math.min(Math.abs(distance) / 1000, 1);
    const volume = 0.1 + normalizedDistance * 0.1; // Volume between 0.1-0.2 based on distance
    playSound(SoundEffect.SCROLL, volume);
  }

  // Easing function for smooth animation
  const easeInOutQuad = (t: number): number => {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  };

  // Animation function
  const animation = (currentTime: number): void => {
    if (startTime === null) {
      startTime = currentTime;
    }

    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);
    const easedProgress = easeInOutQuad(progress);
    const newPosition = startPosition + distance * easedProgress;

    window.scrollTo(0, newPosition);

    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    } else {
      // Make sure we're exactly at the target position
      window.scrollTo(0, targetPosition);

      // If we're targeting a specific section, ensure it's visible and in focus
      if (targetElement) {
        setTimeout(() => {
          // Double-check our position after animation is complete
          const finalOffset = targetElement.getBoundingClientRect().top;
          if (Math.abs(finalOffset) > 50) { // If we're more than 50px off
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 50);
      }
    }
  };

  // Start the animation
  requestAnimationFrame(animation);
};

/**
 * Scrolls to an element with intersection observer and sound effects
 * This is useful for scroll-triggered animations
 * @param selector - The CSS selector for the target element
 * @param threshold - The visibility threshold to trigger the scroll
 * @param enableSound - Whether to play sound effects
 */
export const scrollToElementWhenVisible = (
  selector: string,
  threshold = 0.1,
  enableSound = true
): (() => void) => {
  let observer: IntersectionObserver;
  const soundPlayed = { value: false };

  const setupObserver = () => {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && enableSound && !soundPlayed.value) {
            soundPlayed.value = true;
            playSound(SoundEffect.SCROLL, 0.1);
          }
        });
      },
      { threshold }
    );

    const element = document.querySelector(selector);
    if (element) {
      observer.observe(element);
    } else {
      console.warn(`Element with selector "${selector}" not found`);
    }
  };

  // Setup the observer after a short delay to ensure DOM is ready
  const timerId = setTimeout(setupObserver, 100);

  // Return a cleanup function
  return () => {
    clearTimeout(timerId);
    if (observer) {
      observer.disconnect();
    }
  };
};
